import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { PROJECTS } from "@/data/projects";
import { PictureAsset } from "@/components/site/PictureAsset";
import "@/pages/Work.css";

export default function Work() {
  const reducedMotion = useReducedMotion();
  const rootRef = useRef(null);

  useEffect(() => {
    document.title = "Selected Work — Ōraṇi";
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const targets = root.querySelectorAll("[data-work-reveal]");
    if (reducedMotion || !("IntersectionObserver" in window)) {
      targets.forEach((n) => n.classList.add("is-in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -10% 0px" }
    );
    targets.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [reducedMotion]);

  return (
    <main
      ref={rootRef}
      className="work"
      data-testid="work-page"
      aria-label="Selected work"
    >
      <header className="work__header">
        <h1
          className="work__title"
          data-work-reveal
          style={{ transitionDelay: "120ms" }}
          data-testid="work-title"
        >
          Selected Work
        </h1>
        <p
          className="work__intro"
          data-work-reveal
          style={{ transitionDelay: "260ms" }}
          data-testid="work-intro"
        >
          A culmination of design shaped through lived experiences,
          expressive materiality, and a careful balance of restraint
          and drama.
        </p>
      </header>

      <section className="work__grid" aria-label="Project index">
        {PROJECTS.map((p, i) => {
          const delay = `${(i % 2) * 100 + 40}ms`;
          const cardClass =
            "work__card" +
            (p.tall ? " work__card--tall" : "") +
            (p.private ? " work__card--private" : "");
          if (p.private) {
            return (
              <Link
                key={p.slug}
                to={`/work/${p.slug}`}
                className={cardClass}
                data-work-reveal
                style={{ transitionDelay: delay }}
                data-testid={`work-card-${p.slug}`}
              >
                <div className="work__media-wrap">
                  <PictureAsset
                    base={p.imageBase}
                    alt={`${p.name} — under publication embargo`}
                    className="work__media work__media--blurred"
                    sizes="(min-width: 880px) 45vw, 100vw"
                  />
                  <div className="work__media-veil" aria-hidden="true" />
                  <span className="work__lock" aria-hidden="true">
                    <svg width="14" height="18" viewBox="0 0 14 18" fill="none">
                      <rect
                        x="1"
                        y="7"
                        width="12"
                        height="10"
                        rx="1"
                        stroke="currentColor"
                        strokeWidth="1"
                      />
                      <path
                        d="M3 7V4a4 4 0 1 1 8 0v3"
                        stroke="currentColor"
                        strokeWidth="1"
                      />
                    </svg>
                  </span>
                </div>
                <div className="work__meta">
                  <span className="work__embargo-eyebrow">Under Publication Embargo</span>
                  <h2 className="work__name">{p.name}</h2>
                  <p className="work__type">
                    {p.type}
                    {p.area ? (
                      <>
                        {" "}
                        <span aria-hidden="true">·</span> {p.location}{" "}
                        <span aria-hidden="true">·</span> {p.area}
                      </>
                    ) : (
                      <>
                        {" "}
                        <span aria-hidden="true">·</span> {p.location}
                      </>
                    )}
                  </p>
                  {p.photography ? (
                    <p className="work__photography">
                      Photography by {p.photography}
                    </p>
                  ) : null}
                  <p className="work__descriptor">
                    {p.embargoNote || p.descriptor}
                  </p>
                  <span className="work__cta" data-testid={`work-cta-${p.slug}`}>
                    Request Access
                    <svg
                      width="22"
                      height="10"
                      viewBox="0 0 22 10"
                      aria-hidden="true"
                      className="work__cta-arrow"
                    >
                      <path
                        d="M1 5h20M17 1l4 4-4 4"
                        stroke="currentColor"
                        strokeWidth="1"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </div>
              </Link>
            );
          }
          return (
            <Link
              key={p.slug}
              to={`/work/${p.slug}`}
              className={cardClass}
              data-work-reveal
              style={{ transitionDelay: delay }}
              data-testid={`work-card-${p.slug}`}
            >
              <div className="work__media-wrap">
                <PictureAsset
                  base={p.imageBase}
                  alt={p.name}
                  className="work__media"
                  sizes="(min-width: 880px) 45vw, 100vw"
                />
              </div>
              <div className="work__meta">
                <h2 className="work__name">{p.name}</h2>
                {p.credibility ? (
                  <p
                    className="work__credibility"
                    data-testid={`work-credibility-${p.slug}`}
                  >
                    {p.credibility}
                  </p>
                ) : null}
                <p className="work__type">
                  {p.type}
                  {p.area ? (
                    <>
                      {" "}
                      <span aria-hidden="true">·</span> {p.location}{" "}
                      <span aria-hidden="true">·</span> {p.area}
                    </>
                  ) : (
                    <>
                      {" "}
                      <span aria-hidden="true">·</span> {p.location}
                    </>
                  )}
                </p>
                {p.photography ? (
                  <p className="work__photography">
                    Photography by {p.photography}
                  </p>
                ) : null}
                <p className="work__descriptor">{p.descriptor}</p>
                <span className="work__cta" data-testid={`work-cta-${p.slug}`}>
                  View Project
                  <svg
                    width="22"
                    height="10"
                    viewBox="0 0 22 10"
                    aria-hidden="true"
                    className="work__cta-arrow"
                  >
                    <path
                      d="M1 5h20M17 1l4 4-4 4"
                      stroke="currentColor"
                      strokeWidth="1"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
            </Link>
          );
        })}
      </section>
    </main>
  );
}
